<template>
  <div class="login-texts-container">
    <h3 class="section-title">Login Page Texts</h3>
    <div class="config-card">
      <p class="config-hint intro">
        These texts appear on the login / registration page. Leave a field blank to use the
        app's built-in default. They apply to all languages.
      </p>

      <div class="text-grid">
        <div class="config-item item--wide">
          <label class="config-label">Intro text</label>
          <textarea v-model="texts.intro_text" @change="save" placeholder="(hidden when blank)" class="text-input text-area"></textarea>
          <p class="config-hint">
            Shown at the top of the login page, right below the logo. Use it for a little
            context, e.g. <em>"Absolutely free, no credit card required"</em>. Basic HTML is allowed.
          </p>
        </div>

        <div class="config-item">
          <label class="config-label">Login title</label>
          <input type="text" v-model="texts.email_title" @change="save" placeholder="Sign In or Register" class="text-input"/>
        </div>

        <div class="config-item">
          <label class="config-label">Login subtitle</label>
          <input type="text" v-model="texts.email_subtitle" @change="save" placeholder="Enter your email address to get started" class="text-input"/>
        </div>

        <div class="config-item">
          <label class="config-label">Registration title</label>
          <input type="text" v-model="texts.register_title" @change="save" placeholder="Create Account" class="text-input"/>
          <p class="config-hint">Shown to new visitors after they enter an unknown e-mail address.</p>
        </div>

        <div class="config-item">
          <label class="config-label">Registration subtitle</label>
          <input type="text" v-model="texts.register_subtitle" @change="save" placeholder="It looks like you are new here. Set a password (min. 8 characters, no spaces) to get started." class="text-input"/>
        </div>

        <div class="config-item">
          <label class="config-label">Newsletter signup</label>
          <label class="checkbox-row">
            <input type="checkbox" v-model="texts.newsletter_optin" @change="save"/>
            <span>Ask new accounts to opt in to the newsletter</span>
          </label>
          <p class="config-hint">Shows an (unchecked) checkbox on the registration step. Opt-ins are stored on the user account.</p>
        </div>

        <div class="config-item">
          <label class="config-label">Newsletter checkbox label</label>
          <input type="text" v-model="texts.newsletter_label" @change="save" placeholder="Send me occasional news and updates by e-mail" class="text-input" :disabled="!texts.newsletter_optin"/>
        </div>
      </div>

      <transition name="fade">
        <div v-if="saving" class="config-status saving">Saving...</div>
        <div v-else-if="saved" class="config-status saved">Saved</div>
      </transition>
    </div>

    <!-- Live preview -->
    <h3 class="section-title">Preview</h3>
    <div class="preview-card">
      <div class="preview-logo">Your logo</div>
      <div v-if="texts.intro_text" class="preview-intro" v-html="texts.intro_text"></div>
      <div class="preview-title">{{ texts.email_title || 'Sign In or Register' }}</div>
      <div class="preview-hint">{{ texts.email_subtitle || 'Enter your email address to get started' }}</div>
      <div class="preview-input">you@example.com</div>
      <div v-if="texts.newsletter_optin" class="preview-newsletter">
        <span class="preview-checkbox"></span>
        {{ texts.newsletter_label || 'Send me occasional news and updates by e-mail' }}
      </div>
      <button class="preview-button">Continue</button>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      texts: {
        intro_text: '',
        email_title: '',
        email_subtitle: '',
        register_title: '',
        register_subtitle: '',
        newsletter_optin: false,
        newsletter_label: ''
      },
      saving: false,
      saved: false,
      saveTimeout: null
    }
  },

  methods: {
    load() {
      fetch('/admin/api/v2/config/login_texts')
        .then(resp => {
          if (resp.status === 200) return resp.json()
          if (resp.status === 404) return null
          throw Error(resp.statusText)
        })
        .then(resp => {
          if (resp && resp.data && resp.data.value) {
            const v = resp.data.value
            this.texts = {
              intro_text: v.intro_text ?? '',
              email_title: v.email_title ?? '',
              email_subtitle: v.email_subtitle ?? '',
              register_title: v.register_title ?? '',
              register_subtitle: v.register_subtitle ?? '',
              newsletter_optin: v.newsletter_optin === true,
              newsletter_label: v.newsletter_label ?? ''
            }
          }
        })
        .catch(() => {
          // Silently use defaults if config doesn't exist
        })
    },

    save() {
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout)
      }

      this.saving = true
      this.saved = false

      fetch('/admin/api/v2/config/login_texts', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            value: this.texts,
            is_public: true
          }
        })
      })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.saving = false
          this.saved = true
          this.saveTimeout = setTimeout(() => {
            this.saved = false
          }, 2000)
        })
        .catch(() => {
          this.saving = false
          this.$root.app.showAlert('Error saving login texts', 'danger')
        })
    }
  },

  mounted() {
    this.$root.app.setActivePageTitle('Login Texts', false)
    this.$root.$emit('set-toolbar', 'empty-toolbar')
    this.load()
  }
}
</script>

<style scoped>
.login-texts-container {
  padding: 1.5rem;
  max-width: 1000px;
  margin: 0 auto;
}

.section-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
}

.config-card,
.preview-card {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  padding: 1.5rem;
  position: relative;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
}

.intro {
  margin-top: 0;
  margin-bottom: 1.25rem;
}

.text-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem 1.5rem;
}

.item--wide {
  grid-column: 1 / -1;
}

.config-label {
  font-size: 0.85rem;
  color: #E2E8F0;
  margin-bottom: 0.5rem;
  display: block;
  font-weight: 600;
}

.config-hint {
  font-size: 0.75rem;
  color: #94A3B8;
  margin-top: 0.4rem;
}

.config-hint code {
  background: rgba(0, 0, 0, 0.3);
  padding: 0.05rem 0.3rem;
  border-radius: 3px;
  font-size: 0.72rem;
  color: #20B4FF;
}

.text-input {
  width: 100%;
  padding: 0.45rem 0.6rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 0.85rem;
  box-sizing: border-box;
}

.text-input:focus {
  outline: none;
  border-color: #20B4FF;
}

.text-input.text-area {
  min-height: 4.5rem;
  resize: vertical;
  font-family: inherit;
}

.config-status {
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.config-status.saving {
  color: #9ca3af;
}

.config-status.saved {
  color: #28a745;
  background: #28a74520;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Preview */
.preview-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.preview-logo {
  font-weight: 700;
  font-size: 1.1rem;
  color: #E2E8F0;
  letter-spacing: 0.02em;
  margin-bottom: 0.25rem;
}

.preview-intro {
  font-size: 0.85rem;
  color: #cbd5e1;
  text-align: center;
  max-width: 340px;
}

.preview-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #fff;
  margin-top: 0.5rem;
}

.preview-hint {
  font-size: 0.8rem;
  color: #94A3B8;
}

.preview-input {
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  padding: 0.5rem 1rem;
  min-width: 260px;
  color: #64748b;
  font-size: 0.85rem;
  text-align: left;
}

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #E2E8F0;
  cursor: pointer;
  font-weight: 400;
  margin: 0.2rem 0 0;
}

.checkbox-row input {
  margin: 0;
  cursor: pointer;
}

.text-input:disabled {
  opacity: 0.4;
}

.preview-newsletter {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  color: #94A3B8;
}

.preview-checkbox {
  width: 0.7rem;
  height: 0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  display: inline-block;
}

.preview-button {
  background: linear-gradient(135deg, #20B4FF 0%, #007BFF 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 2rem;
  font-weight: 600;
  cursor: default;
  text-align: center;
  line-height: 1.3;
  min-width: 260px;
}

@media (max-width: 768px) {
  .text-grid {
    grid-template-columns: 1fr;
  }
}
</style>
